import { TradingSystem } from '../../trading-system';
import { AccountCreationQueueJobType } from '../hub-queue-types';
import { SchedulerJob } from './scheduler-job';

export interface CreateAccountPayload {
  purchaseId: string;
  initialFunds?: number;
  initialProfitCredits?: number;
  initialRefundCredits?: number;
  previousProgramId?: string;
  tradingSystem?: TradingSystem;
  accountId?: string;
  programId: string;
  userId: string;
  initialBalance?: number;
  fundedLevel?: number;
}

export interface CreateAccountJob {
  data: CreateAccountPayload;
  type: AccountCreationQueueJobType.CreateAccount;
}

export type AsyncCreateAccountPayload = CreateAccountPayload;

export interface AsyncCreateAccountJob {
  data: AsyncCreateAccountPayload;
  type: AccountCreationQueueJobType.AsyncCreateAccount;
}

export interface CreatePendingAccountsJob {
  data?: SchedulerJob;
  type: AccountCreationQueueJobType.CreatePendingAccounts;
}

export const ACCOUNT_CREATION_CORRELATION_KEY_SEPARATOR = ':';
export const ACCOUNT_CREATION_CORRELATION_HASH_DERIVATION =
  'sha256(correlationKey)';

/**
 * Agreed cross-service SLA: producers must exhaust their own retry window
 * before emitting an AccountCreationFailed event. All producer microservices
 * (MetaTrader, TraderEvolution, cTrader, BlackArrow) MUST honour this value.
 */
export const ASYNC_ACCOUNT_CREATION_PRODUCER_RETRY_WINDOW_MINUTES = 60;

export interface AccountCreationLifecycleCorrelationPayload {
  purchaseId: string;
  programId: string;
  userId: string;
  tradingSystem: TradingSystem;
}

export interface AccountCreationLifecycleCorrelationHashPayload {
  correlationHash: string;
}

export type AccountCreationLifecyclePayload =
  AccountCreationLifecycleCorrelationPayload &
    AccountCreationLifecycleCorrelationHashPayload;

export const getAccountCreationLifecycleCorrelationKey = ({
  purchaseId,
  programId,
  userId,
  tradingSystem,
}: AccountCreationLifecycleCorrelationPayload): string =>
  [purchaseId, programId, userId, tradingSystem].join(
    ACCOUNT_CREATION_CORRELATION_KEY_SEPARATOR,
  );

export interface AccountCreationCompletedOutcomePayload {
  status: 'success';
  tsAccountId: string;
  tsAccount?: string;
  tsUserId?: string;
  isNewUser: boolean;
  userLogin?: string;
  userPassword?: string;
}

export type AccountCreationCompletedPayload =
  AccountCreationLifecyclePayload & {
    outcome: AccountCreationCompletedOutcomePayload;
  };

export interface AccountCreationCompletedJob {
  data: AccountCreationCompletedPayload;
  type: AccountCreationQueueJobType.AccountCreationCompleted;
}

export enum AccountCreationFailureReasonCode {
  UPSTREAM_VALIDATION_ERROR = 'UPSTREAM_VALIDATION_ERROR',
  UPSTREAM_REJECTED = 'UPSTREAM_REJECTED',
  UPSTREAM_UNAVAILABLE = 'UPSTREAM_UNAVAILABLE',
  TRANSPORT_ERROR = 'TRANSPORT_ERROR',
  TIMEOUT = 'TIMEOUT',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  MAPPING_ERROR = 'MAPPING_ERROR',
  RETRY_WINDOW_EXHAUSTED = 'RETRY_WINDOW_EXHAUSTED',
  UNKNOWN = 'UNKNOWN',
}

export interface AccountCreationFailedOutcomePayload {
  status: 'failed';
  reasonCode?: AccountCreationFailureReasonCode;
  reasonMessage?: string;
}

export type AccountCreationFailedPayload = AccountCreationLifecyclePayload & {
  outcome: AccountCreationFailedOutcomePayload;
};

export interface AccountCreationFailedJob {
  data: AccountCreationFailedPayload;
  type: AccountCreationQueueJobType.AccountCreationFailed;
}

export interface RetryAsyncAccountsCreationJob {
  data?: SchedulerJob;
  type: AccountCreationQueueJobType.RetryAsyncAccountsCreation;
}

/** Job types that run on a schedule and need no payload data. */
const SCHEDULER_ONLY_ACCOUNT_CREATION_JOB_TYPES: AccountCreationQueueJobType[] =
  [
    AccountCreationQueueJobType.RetryAsyncAccountsCreation,
    AccountCreationQueueJobType.CreatePendingAccounts,
  ];

/** Job types that must carry a full CreateAccountPayload. */
const PAYLOAD_REQUIRED_ACCOUNT_CREATION_JOB_TYPES: AccountCreationQueueJobType[] =
  [
    AccountCreationQueueJobType.CreateAccount,
    AccountCreationQueueJobType.AsyncCreateAccount,
  ];

/**
 * Returns null when the payload is valid for the given job type, or a
 * human-readable error string when validation fails.
 *
 * Rules:
 *  - Scheduler-only types (RetryAsyncAccountsCreation, CreatePendingAccounts)
 *    never require a payload.
 *  - CreateAccount / AsyncCreateAccount require userId, purchaseId, programId.
 *  - Lifecycle events (AccountCreationCompleted, AccountCreationFailed) must
 *    not be triggered externally.
 */
export function validateAccountCreationJobPayload(
  jobType: AccountCreationQueueJobType,
  payload: unknown,
): string | null {
  if (SCHEDULER_ONLY_ACCOUNT_CREATION_JOB_TYPES.includes(jobType)) {
    return null;
  }

  if (PAYLOAD_REQUIRED_ACCOUNT_CREATION_JOB_TYPES.includes(jobType)) {
    const data = payload as Partial<CreateAccountPayload> | undefined;
    if (!data?.userId || !data?.purchaseId || !data?.programId) {
      return `Job type "${jobType}" requires payload with userId, purchaseId, and programId`;
    }
    return null;
  }

  return `Job type "${jobType}" cannot be triggered externally`;
}
